// @ts-strict-ignore
import React, { useState, useMemo } from 'react'
import { Table } from '../../../components/ui/Table'
import { Link } from 'react-router-dom'
import { cents } from '../../../util/formatters'
import { Column } from '../../../types/table'
import type { Models } from '../../../types/profile'
import {
    Params,
    TableHooks,
    InnerPagination,
} from '../../../packages/blueprint-components/src/useTableHooks'

interface ProfilesTableProps {
    profiles: Models.Profile[]
}

export const ProfilesTable = ({ profiles }: ProfilesTableProps) => {
    const [searchQuery, setSearchQuery] = useState('')
    const [params, setParamsState] = useState<Params>({})

    const sort = params.sort as string | undefined

    const filteredAndSorted = useMemo(() => {
        let items = profiles ?? []

        if (searchQuery) {
            const q = searchQuery.toLowerCase()
            items = items.filter(
                (p) =>
                    String(p.profile_id).toLowerCase().includes(q) ||
                    (p.name ?? '').toLowerCase().includes(q) ||
                    (p.country_code ?? '').toLowerCase().includes(q),
            )
        }

        if (sort) {
            const desc = sort.startsWith('-')
            const key = desc ? sort.slice(1) : sort
            items = [...items].sort((a, b) => {
                const aVal = a[key] ?? ''
                const bVal = b[key] ?? ''
                if (typeof aVal === 'number' && typeof bVal === 'number') {
                    return desc ? bVal - aVal : aVal - bVal
                }
                return desc
                    ? String(bVal).localeCompare(String(aVal))
                    : String(aVal).localeCompare(String(bVal))
            })
        }

        return items
    }, [profiles, searchQuery, sort])

    const emptyPagination: InnerPagination = {
        current: null,
        last_page: null,
        from: null,
        to: null,
        total: null,
    }

    const tableHooks: TableHooks<Models.Profile> = {
        totals: null,
        loading: false,
        items: filteredAndSorted,
        params,
        pagination: emptyPagination,
        commit: true,
        setParams: (paramsOrFn: any) => {
            if (typeof paramsOrFn === 'function') {
                setParamsState((prev) => paramsOrFn(prev))
            } else {
                setParamsState(paramsOrFn)
            }
        },
        reload: () => {},
        rerender: () => {},
        setPage: () => {},
        setQuery: (query: string) => setSearchQuery(query),
        setFilter: () => {},
        mutateItems: () => {},
        setCommit: () => {},
    }

    const columns: Column<Models.Profile, any>[] = [
        {
            key: 'profile_id',
            cell: ({ profile_id }) => (
                <Link to={`/admin/profiles/${profile_id}`}>{profile_id}</Link>
            ),
        },
        {
            key: 'country_code',
            title: 'Marketplace',
        },
        {
            key: 'total_ad_spend_30_days_usd',
            title: 'Total Ad Spend (30 Days USD)',
            formatter: cents,
        },
        { key: 'name' },
        { key: 'nickname' },
    ]

    return (
        <Table
            name="Profiles"
            showHeader
            className=""
            filters={null}
            canSelect={false}
            columnSelection={false}
            columns={columns}
            tableHooks={tableHooks}
            onSelectChange={null}
            search
            searchPlaceholder="Search by profile ID, name, or country..."
            height={300}
        />
    )
}
